import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { InvoiceModel } from '@frontend/api-invoices';
import { formatBirthDate, formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { PaymentsFeatureFlags, useMultiLocationUtils } from '@frontend/payments-hooks';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Chip, Info, Text } from '@frontend/design-system';

const styles = {
  detailsRowBox: css`
    display: flex;
    gap: ${theme.spacing(3)};
    align-items: center;
  `,
  detailsSection: css`
    margin-bottom: ${theme.spacing(3)};
  `,
  detailsContainer: css`
    display: grid;
    overflow-y: auto;
    grid-template-columns: repeat(1, max-content);
    grid-gap: ${theme.spacing(1, 3)};
    margin-bottom: ${theme.spacing(3)};
    @media (min-width: ${breakpoints.small.min}px) {
      grid-template-columns: repeat(2, max-content);
    }
  `,
  detailsInfo: (isMobile: boolean) => css`
    margin-bottom: ${isMobile ? '0px' : theme.spacing(1)};

    @media (min-width: ${breakpoints.small.min}px) {
      margin-bottom: ${theme.spacing(0)};
    }
  `,
  noMargin: css`
    margin: 0;
  `,
  locationChip: css`
    max-width: none;
  `,
  detailsTitle: css`
    display: flex;
    gap: ${theme.spacing(1)};
    align-items: center;
  `,
  infoText: css`
    color: ${theme.colors.text.subdued};
  `,
};

interface DetailsProps {
  invoice: InvoiceModel;
  personEmail: string | undefined;
}

export const Details = ({ invoice, personEmail }: DetailsProps) => {
  const { t } = useTranslation('payments');
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const { getLocationName } = useMultiLocationUtils();
  const obpNewFieldsFF = getFeatureFlagValue(PaymentsFeatureFlags.ObpNewFields);

  return (
    <section css={styles.detailsContainer}>
      {selectedLocationIds.length > 1 && (
        <DetailRow
          title={t('Location')}
          detail={<Chip.SingleChip css={styles.locationChip}>{getLocationName(invoice.locationId)}</Chip.SingleChip>}
        />
      )}
      <DetailRow title={t('Provider')} detail={invoice.providerName} />
      <DetailRow title={t('Date Created')} detail={formatDate(invoice.billedAt, 'MMMM D, YYYY')} />
      <DetailRow title={t('Customer Email')} detail={personEmail} />
      {obpNewFieldsFF && invoice.person.birthdate && (
        <DetailRow title={t('D.O.B.')} detail={formatBirthDate(invoice.person.birthdate, 'MMMM D, YYYY')} />
      )}
      {obpNewFieldsFF && invoice.person.patientIdentifier && (
        <DetailRow title={t('Invoice/Account Number')} detail={invoice.person.patientIdentifier} />
      )}
    </section>
  );
};

const DetailRow = ({
  title,
  detail,
  info,
}: {
  title: string;
  detail: string | undefined | ReactNode;
  info?: string;
}) => {
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  return (
    <div css={isMobile ? styles.detailsRowBox : {}}>
      <div css={styles.detailsTitle}>
        <Text color='subdued' size='medium' css={styles.noMargin}>
          {title}
        </Text>
        {info && <Info css={styles.infoText}>{info}</Info>}
      </div>
      {typeof detail === 'string' ? (
        <Text size='medium' css={[styles.noMargin, styles.detailsInfo(isMobile)]}>
          {detail || '-'}
        </Text>
      ) : (
        <div css={[styles.noMargin, styles.detailsInfo(isMobile)]}>{detail || '-'}</div>
      )}
    </div>
  );
};
